import Mustache from 'mustache'
import React from 'react'

import { type CurrencyCode } from '@mondough/money'

import { CopyContext } from './context'
import {
  BadPluralError,
  CopyNotFoundError,
  FatalCopyError,
  MissingCopyBundleError,
  UnrecognisedCopyError,
  VariableSubstitutionError,
} from './errors'
import type {
  CardinalForms,
  Components,
  CopyClient,
  CopyKey,
  CopyWithComponent,
  CopyWithVariable,
  OrdinalForms,
  PluralCopy,
  PluralType,
  PluralVariable,
  SimpleCopy,
  SupportedLocales,
  Variables,
} from './types'

// reexports for public consumption
export { Copy } from './components'
export { CopyProvider, MockCopyProvider } from './context'
export type { SupportedLocales } from './types'

/**
 * Container class for a plural variable
 */
class PluralVar implements PluralVariable {
  constructor(
    private name: string,
    private value: number,
    private rule: Intl.LDMLPluralRule = 'other',
  ) {}

  public get variable() {
    return { [this.name]: this.value }
  }

  public get pluralRule() {
    return this.rule
  }
}

type Args =
  | {
      key: CopyKey
      message: CopyWithVariable | CopyWithComponent
      variables?: Variables<CopyWithVariable>
      components?: Components<CopyWithComponent>
    }
  | {
      key: CopyKey
      message: CardinalForms | OrdinalForms
      plural: PluralVariable
      components?: Components<CopyWithComponent>
    }
  | { key: CopyKey; message: string }

// scaffolded implementation to get a feel for how it would look/behave
export const useCopy = (): CopyClient => {
  const ctx = React.useContext(CopyContext)
  const { copy, locale, appName } = ctx

  if (!appName) {
    throw new Error(
      'useCopy can only be used inside a CopyProvider. Did you forget to wrap your app with <CopyProvider>?',
    )
  }

  // The main copy function
  const copyClient = ({ key, message, ...params }: Args) => {
    try {
      // no copy bundle available
      if (!copy) {
        throw new MissingCopyBundleError(ctx)
      }

      const copyValue = copy[key]

      // couldn't find copy for given key
      if (!copyValue) {
        throw new CopyNotFoundError(key)
      }

      const localisedCopy = copyValue.localized_messages[locale]

      // couldn't find copy for given locale
      if (!localisedCopy) {
        throw new CopyNotFoundError(key, locale)
      }

      // We've found the copy key in the bundle, it has a variant for
      // current locale, and it is a SIMPLE copy type
      if (isSimpleForm(localisedCopy)) {
        const simpleContent = localisedCopy.simple.content

        // We need to do some interpolation
        if ('variables' in params || 'components' in params) {
          return substituteVariables(simpleContent, params)
        }

        return simpleContent
      }

      // We've found the copy key in the bundle, it has a variant for
      // current locale, and it is a PLURAL_FORMS copy type
      if (isPluralForm(localisedCopy)) {
        const pluralContent = localisedCopy.plural_forms

        // The params supplied match the form required for plurals
        if (isPluralParam(params)) {
          // Fallback to the *other* form if the desired is one not found
          const copyString =
            pluralContent[params.plural.pluralRule] ?? pluralContent.other

          return typeof copyString === 'string'
            ? substituteVariables(copyString, params) // the 'other' form
            : substituteVariables(copyString.content, params) // the desired form
        } else {
          // We don't recognise the plural argument
          throw new BadPluralError(key, localisedCopy, params)
        }
      }

      // We've reached somewhere we never should
      throw new UnrecognisedCopyError(key, locale, localisedCopy, params)
    } catch (error) {
      // Do something with the other errors that might've occurred
      // MissingCopyBundleError
      // CopyNotFoundError
      // BadPluralError
      // UnrecognisedCopyError

      // Try to use fallback copy
      return getFallbackCopy(key, locale, message, params)
    }
  }

  copyClient.Cardinal = 'cardinal' as const
  copyClient.Ordinal = 'ordinal' as const

  copyClient.plural = (
    variable: string,
    value: number,
    type: PluralType = copyClient.Cardinal,
  ): PluralVariable => {
    const pluralRules = new Intl.PluralRules(locale, { type })
    const pluralRule = pluralRules.select(value)
    return new PluralVar(variable, value, pluralRule)
  }

  copyClient.cardinals = (forms: CardinalForms) => {
    return forms
  }

  copyClient.ordinals = (forms: OrdinalForms) => {
    return forms
  }

  copyClient.money = (value: number, currencyCode: CurrencyCode) => {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      useGrouping: true,
    })

    return formatter.format(value)
  }

  /**
   * Utility to mark a hardcoded string as copy
   *
   * This can be used to identify all copy that needs to be considered
   * for use of lib.copy. It is intended to be useful for both: humans
   * (to not miss any potential copy) and AI agents (to be quicker at
   * identifing copy).
   *
   * It is only intended to be used as part of a workflow for copy
   * migration, and it should eventually be replaced by an appropriate
   * usage of lib.copy.
   */
  copyClient.TODO = (copy: string) => {
    return copy
  }

  return copyClient
}

// private helpers

function getFallbackCopy(
  key: CopyKey,
  locale: SupportedLocales,
  fallback: string | CardinalForms | OrdinalForms,
  params: {
    plural?: PluralVariable
    variables?: Variables<string> | PluralVariable
    components?: Components<string>
  },
) {
  try {
    // static string fallback, i.e. no params
    if (
      !params.variables &&
      !params.components &&
      typeof fallback === 'string'
    ) {
      return fallback
    }

    // we have params, so do interpolation
    // simple form
    if (typeof fallback === 'string') {
      return substituteVariables(fallback, params)
    }

    // plural form
    if (isPluralParam(params)) {
      const fallbackPlural = fallback[params.plural.pluralRule]

      return fallbackPlural
        ? substituteVariables(fallbackPlural, params)
        : substituteVariables(fallback.other, params)
    } else {
      throw new BadPluralError(key, fallback, params)
    }

    throw new FatalCopyError(key, locale, fallback, params)
  } catch (err) {
    if (err instanceof FatalCopyError) {
      throw err
    } else {
      // we can't return any copy given the data provided, so fail loud
      throw new FatalCopyError(key, locale, fallback, params, err)
    }
  }
}

function substituteVariables(
  copy: string,
  params: {
    plural?: PluralVariable
    variables?: Variables<CopyWithVariable> | PluralVariable
    components?: Components<CopyWithComponent>
  },
) {
  let copyTemplate = copy
  // A string will be returned if there are no components used in
  // substitution. If there are, then a ReactFragment will be returned
  let result: string | JSX.Element = ''

  try {
    // Deal with regular string substitution first, either simple variables
    // or a plural form
    if (params.plural) {
      copyTemplate = Mustache.render(copyTemplate, params.plural.variable)
      result = copyTemplate
    } else if (params.variables) {
      copyTemplate = Mustache.render(copyTemplate, params.variables)
      result = copyTemplate
    }

    // Now replace any referenced components
    if (params.components) {
      /*
        Given the input string
        <emphasise>{{ greeting }}</emphasise>, have a <emphasise>{{ adjective }}</emphasise> day
        with the string substitution already having happened above, using
        { greeting: 'Howdy partner', adjective: 'special'}
        the string after substitution will be:
        <emphasise>Howdy partner</emphasise>, have a <emphasise>special</emphasise> day

        We'll create a regex that matches all component tags, and then use
        that to split the string into its atomic constituent parts.

        Assuming the components value passed is 
        { emphasise: ({ children }) => <strong>{children}</strong> },
        
        the regex will match the following:
        <emphasise>Howdy partner</emphasise>

        and

        <emphasise>special</emphasise>

        which will be looped over to create an array of strings and React elements stored in the const parts.

        Each loop iteration will:
        - add the text before the matched component to the array as a string
        - add the matched component as a React element, passing the children
          to the component function
        - add the text after the matched component to the array as a string

        So, the parts array will be:
        [
          {
            type: 'strong',
            props: {
              children: 'Howdy partner'
            }
          },
          ', have a ',
          {
            type: 'strong',
            props: {
              children: 'special'
            }
          },
          ' day'
        ]

        This will then be returned as a React fragment, which will be rendered
        as:
        <strong>Howdy partner</strong>, have a <strong>special</strong> day
      */
      // Parse the string to find all component tags and convert them to React elements
      const parts: (string | JSX.Element)[] = []
      const remainingString = copyTemplate
      let keyCounter = 0

      // Build a regex that matches all component tags
      const componentNames = Object.keys(params.components)
      const tagPattern = new RegExp(
        `<(${componentNames.join('|')})>(.*?)<\\/\\1>`,
        'g',
      )

      let lastIndex = 0
      let match: RegExpExecArray | null

      while ((match = tagPattern.exec(remainingString)) !== null) {
        // Add any text before this match
        if (match.index > lastIndex) {
          const textBefore = remainingString.slice(lastIndex, match.index)
          if (textBefore) {
            parts.push(textBefore)
          }
        }

        // Create the React element for this component
        const componentName = match[1]
        const children = match[2]
        const component = componentName
          ? params.components[componentName]
          : undefined

        if (typeof component === 'function') {
          parts.push(
            React.cloneElement(component({ children }), { key: keyCounter++ }),
          )
        } else {
          // Fallback to the original text if component is not a function
          parts.push(match[0])
        }

        lastIndex = tagPattern.lastIndex
      }

      // Add any remaining text after the last match
      if (lastIndex < remainingString.length) {
        const textAfter = remainingString.slice(lastIndex)
        if (textAfter) {
          parts.push(textAfter)
        }
      }

      // If we found any components, return a React fragment
      if (parts.some((part) => typeof part !== 'string')) {
        result = <>{parts}</>
      }
    }

    return result
  } catch (err) {
    throw new VariableSubstitutionError(copy, params, err)
  }
}

function isSimpleForm(copy: SimpleCopy | PluralCopy): copy is SimpleCopy {
  return copy !== undefined && copy.type === 'SIMPLE'
}

function isPluralForm(copy: SimpleCopy | PluralCopy): copy is PluralCopy {
  return copy !== undefined && copy.type === 'PLURAL_FORMS'
}

function isPluralParam(params: {
  plural?: PluralVariable
  variables?: Variables<string>
  components?: Components<string>
}): params is {
  plural: PluralVariable
  components?: Components<string>
} {
  return 'plural' in params && params.plural instanceof PluralVar
}
